import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      toast({
        title: t('resetPassword.invalidLinkTitle'),
        description: t('resetPassword.invalidLinkDescription'),
        variant: 'destructive'
      });
      setTimeout(() => navigate('/forgot-password'), 3000);
    } else {
      setToken(tokenParam);
    }
  }, [searchParams, navigate, toast, t]);

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) {
      errors.push('minLength');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('lowercase');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('uppercase');
    }
    if (!/\d/.test(password)) {
      errors.push('number');
    }
    return errors;
  };

  const handlePasswordChange = (value) => {
    setNewPassword(value);
    setValidationErrors(validatePassword(value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validationErrors.length > 0) {
      toast({
        title: t('resetPassword.requirementsTitle'),
        description: t('resetPassword.requirementsDescription'),
        variant: 'destructive'
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: t('resetPassword.mismatchTitle'),
        description: t('resetPassword.mismatchDescription'),
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      
      setSuccess(true);
      toast({
        title: t('resetPassword.successTitle'),
        description: t('resetPassword.successDescription')
      });
      
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      console.error('Reset password error:', error);
      const errorMessage = error.response?.data?.detail || t('resetPassword.resetFailed');
      toast({
        title: t('common.error'),
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAF8F5] via-white to-[#FAF8F5] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="font-semibold text-gray-900 mb-2">{t('resetPassword.invalidResetLink')}</h3>
            <p className="text-sm text-gray-600">{t('resetPassword.redirecting')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF8F5] via-white to-[#FAF8F5] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">{t('resetPassword.createTitle')}</CardTitle>
          <CardDescription>
            {success 
              ? t('resetPassword.descriptionSuccess')
              : t('resetPassword.descriptionForm')}
          </CardDescription>
        </CardHeader>

        {success ? (
          <CardContent className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <h3 className="font-semibold text-green-900 mb-2">{t('resetPassword.completeHeading')}</h3>
              <p className="text-sm text-green-800 mb-4">
                {t('resetPassword.canLogin')}
              </p>
              <p className="text-xs text-green-700">
                {t('resetPassword.redirectingLogin')}
              </p>
            </div>

            <Link to="/login" className="block">
              <Button className="w-full bg-[#be1f1a] hover:bg-[#a71b17]">
                {t('resetPassword.goToLogin')}
              </Button>
            </Link>
          </CardContent>
        ) : (
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-gray-700">
                  {t('resetPassword.newPasswordLabel')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('resetPassword.newPasswordPlaceholder')}
                    value={newPassword}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="confirm" className="text-sm font-medium text-gray-700">
                  {t('resetPassword.confirmLabel')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    id="confirm"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder={t('resetPassword.confirmPlaceholder')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {newPassword && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-xs font-semibold text-blue-900 mb-2">{t('resetPassword.requirementsLabel')}</p>
                  <ul className="text-xs text-blue-800 space-y-1">
                    {[
                      { label: t('resetPassword.reqMinLength'), valid: newPassword.length >= 8 },
                      { label: t('resetPassword.reqLowercase'), valid: /[a-z]/.test(newPassword) },
                      { label: t('resetPassword.reqUppercase'), valid: /[A-Z]/.test(newPassword) },
                      { label: t('resetPassword.reqNumber'), valid: /\d/.test(newPassword) }
                    ].map((req, index) => (
                      <li key={index} className={`flex items-center gap-2 ${
                        req.valid ? 'text-green-700' : 'text-gray-600'
                      }`}>
                        <span className={`w-4 h-4 rounded-full flex items-center justify-center ${
                          req.valid ? 'bg-green-200' : 'bg-gray-200'
                        }`}>
                          {req.valid && '✓'}
                        </span>
                        {req.label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {newPassword && confirmPassword && newPassword !== confirmPassword && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs text-red-800">
                    {t('resetPassword.mismatchHint')}
                  </p>
                </div>
              )}
            </CardContent>

            <CardFooter className="flex flex-col space-y-3">
              <Button
                type="submit"
                className="w-full bg-[#be1f1a] hover:bg-[#a71b17]"
                disabled={loading || validationErrors.length > 0 || newPassword !== confirmPassword}
              >
                {loading ? t('resetPassword.resetting') : t('resetPassword.resetButton')}
              </Button>
              
              <Link to="/login" className="w-full">
                <Button type="button" variant="outline" className="w-full">
                  {t('common.cancel')}
                </Button>
              </Link>
            </CardFooter>
          </form>
        )}
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
